import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import options from "./js/options.js";
import light from "./js/light.js";
import baseFloor from "./js/base-floor.js";
import loadingManager from "./js/loader.js";
import {pdfCreate} from "./js/pdf.js";
import {ARButton, EffectComposer, EXRLoader, RenderPass, SSAOPass, SSRPass, UnrealBloomPass} from "three/addons";
import theme from "./js/theme.js";
import {initAIImageGenerator} from "./js/ai.js";


// const gui = new GUI()

const canvasContainer = document.querySelector('#canvas-container');

const canvasContainerSize = canvasContainer.getBoundingClientRect()

const canvas = document.querySelector('canvas.webgl')

const scene = new THREE.Scene()

const isLarge = canvasContainer.getBoundingClientRect().width > 600


const dracoLoader = new DRACOLoader(loadingManager)

dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)

gltfLoader.setDRACOLoader(dracoLoader)

const sizes = {
    width: canvasContainerSize.width,
    height: canvasContainerSize.height
}


light(scene)
baseFloor(THREE, scene)


const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)

isLarge ? camera.position.set(3, 6, 9): camera.position.set(6, 6, 14)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, 5, 0)
controls.enableDamping = true
controls.enablePan = false

controls.minDistance = 1
controls.maxDistance = isLarge ? 20: 20
controls.maxPolarAngle = Math.PI / 2
// controls.minAzimuthAngle =  Math.PI / 9
// controls.maxAzimuthAngle = Math.PI / 1.3


let model = null

let originalMaterial = null


window.addEventListener('resize', () =>
{
    sizes.width = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3))

    backgroundMaterial.uniforms.uAspect.value = sizes.width / sizes.height
})

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    preserveDrawingBuffer: true,
    alpha: true
})
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setClearColor('#ffffff')
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.outputColorSpace = THREE.SRGBColorSpace;


const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();


new EXRLoader().load('./models/urban_street_01_2k.exr', function (texture) {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    scene.environment = texture;
});


const backgroundMaterial = new THREE.ShaderMaterial({
    depthTest: false,
    depthWrite: false,
    uniforms: {
        centerColor: { value: new THREE.Color(0x999999) },
        edgeColor: { value: new THREE.Color(0x999999) },
        uAspect: { value: sizes.width / sizes.height }
    },
    vertexShader: `
        varying vec2 vUv; 
        void main() { 
            vUv = uv; 
            gl_Position = vec4(position.xy, 1.0, 1.0); 
        }`,
    fragmentShader: `
        varying vec2 vUv;
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        uniform float uAspect;
        float random(vec2 coords) { 
            return fract(sin(dot(coords.xy, vec2(12.9898, 78.233))) * 43758.5453); 
        }
        void main() {
            vec2 uv = vUv;
            uv.x = (vUv.x - 0.5) * uAspect + 0.5;
            float factor = smoothstep(0.0, 1.2, distance(uv, vec2(0.5)));
            vec3 finalColor = mix(centerColor, edgeColor, factor);
            float noise = (random(vUv) - 0.5) * (1.0 / 255.0);
            gl_FragColor = vec4(finalColor + noise, 1.0);
        }`
})
const backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backgroundMaterial)
backgroundMesh.frustumCulled = false
backgroundMesh.renderOrder = -1
scene.add(backgroundMesh)

theme(scene, backgroundMaterial)



const clock = new THREE.Clock()
let previousTime = 0

const state = {
    currentModel: null
};

const modelsLibrary = {}


const assets = [
    { name: '1', url: './models/1.glb' },
    { name: '2', url: './models/2.glb' },
    { name: '3', url: './models/3.glb' },
    { name: '4', url: './models/4.glb' },
    { name: '5', url: './models/5.glb' },
]

assets.forEach(asset => {
    gltfLoader.load(asset.url, async (gltf) => {
        modelsLibrary[asset.name] = gltf.scene; // Сохраняем модель по ключу


        if (asset.name === '1') {
            state.currentModel = modelsLibrary['1']
            state.currentModel.traverse( (child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;

                }
            });

            state.currentModel.position.y = 3
            state.currentModel.scale.set(4, 4, 4)

            scene.add(state.currentModel)
        }

        if (Object.keys(modelsLibrary).length === 5) {
            options(state, modelsLibrary, scene, camera)
            await pdfCreate(renderer, camera, scene, state,  backgroundMaterial)
        }
    });
});

initAIImageGenerator({
    renderer,
    camera,
    scene,
});


renderer.xr.enabled = true;

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

if (isIOS) {
    const iosButton = document.createElement('button');
    iosButton.id = 'ARButtonIOS';


    iosButton.onclick = () => {
        const anchor = document.createElement('a');
        anchor.setAttribute('rel', 'ar');
        anchor.appendChild(document.createElement('img'));

        const urlParams = new URLSearchParams(window.location.search);

        const materialParam = urlParams.get('material');

        if (!materialParam || materialParam === '1') anchor.setAttribute('href', './models/1.usdz');
        if ( materialParam === '2') anchor.setAttribute('href', './models/2.usdz');
        if ( materialParam === '3') anchor.setAttribute('href', './models/3.usdz');
        if ( materialParam === '4') anchor.setAttribute('href', './models/4.usdz');
        if ( materialParam === '5') anchor.setAttribute('href', './models/5.usdz');

        anchor.click();
    };

    canvasContainer.appendChild(iosButton);

} else {
    const webXRButton = ARButton.createButton(renderer, {
        requiredFeatures: ['hit-test']
    });
    canvasContainer.appendChild(webXRButton);
}


const reticle = new THREE.Mesh(
    new THREE.RingGeometry(0.1, 0.12, 32).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: 0x00ff00 })
);
reticle.matrixAutoUpdate = false;
reticle.visible = false;
scene.add(reticle);

let hitTestSource = null;
let hitTestSourceRequested = false;

renderer.setAnimationLoop((time, frame) => {
    if (frame) {
        const session = renderer.xr.getSession();

        if (hitTestSourceRequested === false) {
            session.requestReferenceSpace('viewer').then((referenceSpace) => {
                session.requestHitTestSource({ space: referenceSpace }).then((source) => {
                    hitTestSource = source;
                });
            });
            hitTestSourceRequested = true;
        }

        if (hitTestSource) {
            const hitTestResults = frame.getHitTestResults(hitTestSource);
            if (hitTestResults.length) {
                const hit = hitTestResults[0];
                const referenceSpace = renderer.xr.getReferenceSpace();
                const pose = hit.getPose(referenceSpace);

                reticle.visible = true;
                reticle.matrix.fromArray(pose.transform.matrix);
                if (model) {
                    model.visible = true;

                    model.position.setFromMatrixPosition(reticle.matrix);
                    // model.position.y = -1;
                    const lookAtTarget = new THREE.Vector3(camera.position.x, model.position.y, camera.position.z);
                    model.lookAt(lookAtTarget);
                }
            } else {
                reticle.visible = false;
            }
        }
    }

    renderer.render(scene, camera);
});

renderer.xr.addEventListener('sessionstart', () => {
    controls.enabled = false;

    model.position.z = -3;
    model.position.y = -1;
});

renderer.xr.addEventListener('sessionend', () => {
    if (model) {
        model.position.set(0, 0.3, 0);
        model.rotation.y = Math.PI/2 + 0.6
        model.scale.set(1, 1, 1);
        model.visible = true;
    }

    controls.enabled = true;

    camera.position.set(9, 3, 4);
    controls.update();

    if (reticle) reticle.visible = false;

    hitTestSourceRequested = false;
    hitTestSource = null;

});



const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    // if (model && autoRotate) {
    //     model.rotation.y += deltaTime * 0.3
    // }

    controls.update()


    renderer.render(scene, camera)

    window.requestAnimationFrame(tick)
}

tick()